/**
 * useReferencePreview — Hook for managing preview card state
 *
 * Handles the hover bridge pattern: a 120ms timeout on mouse leave that
 * cancels if the mouse enters the preview card. This prevents flickering
 * when moving between trigger and preview.
 */

import { useState, useRef, useCallback, type RefObject } from 'react';
/** What kind of thing the reference points at: a citation, a source, a Matter. */
export type ReferenceKind = string;

export interface PreviewState<T = unknown> {
  visible: boolean;
  targetRef: RefObject<HTMLElement | null>;
  id: string | null;
  type: ReferenceKind | null;
  data: T | null;
}

export interface UseReferencePreviewOptions {
  /** Delay before hiding the preview (hover bridge timeout) */
  delay?: number;
}

export interface UseReferencePreviewReturn<T = unknown> {
  /** Current preview state */
  previewState: PreviewState<T>;
  /** Show the preview for a target element */
  showPreview: (
    targetRef: RefObject<HTMLElement | null>,
    id: string,
    type: ReferenceKind,
    data: T
  ) => void;
  /** Schedule hiding the preview (starts the hover bridge timeout) */
  scheduleHide: () => void;
  /** Cancel the scheduled hide (called when mouse enters preview) */
  cancelHide: () => void;
  /** Immediately hide the preview */
  hidePreview: () => void;
  /** Props to spread on the preview component */
  previewProps: {
    visible: boolean;
    targetRef: RefObject<HTMLElement | null>;
    onMouseEnter: () => void;
    onMouseLeave: () => void;
  };
  /** Create trigger props for an inline reference */
  getTriggerProps: (
    ref: RefObject<HTMLElement | null>,
    id: string,
    type: ReferenceKind,
    data: T
  ) => {
    ref: RefObject<HTMLElement | null>;
    active: boolean;
    onMouseEnter: () => void;
    onMouseLeave: () => void;
  };
}

export function useReferencePreview<T = unknown>(
  options: UseReferencePreviewOptions = {}
): UseReferencePreviewReturn<T> {
  const { delay = 120 } = options;

  const [previewState, setPreviewState] = useState<PreviewState<T>>({
    visible: false,
    targetRef: { current: null },
    id: null,
    type: null,
    data: null,
  });

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelHide = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const showPreview = useCallback(
    (
      targetRef: RefObject<HTMLElement | null>,
      id: string,
      type: ReferenceKind,
      data: T
    ) => {
      cancelHide();
      setPreviewState({
        visible: true,
        targetRef,
        id,
        type,
        data,
      });
    },
    [cancelHide]
  );

  const hidePreview = useCallback(() => {
    cancelHide();
    setPreviewState((prev) => ({
      ...prev,
      visible: false,
    }));
  }, [cancelHide]);

  const scheduleHide = useCallback(() => {
    cancelHide();
    timeoutRef.current = setTimeout(() => {
      setPreviewState((prev) => ({
        ...prev,
        visible: false,
      }));
    }, delay);
  }, [delay, cancelHide]);

  const previewProps = {
    visible: previewState.visible,
    targetRef: previewState.targetRef,
    onMouseEnter: cancelHide,
    onMouseLeave: scheduleHide,
  };

  const getTriggerProps = useCallback(
    (
      ref: RefObject<HTMLElement | null>,
      id: string,
      type: ReferenceKind,
      data: T
    ) => ({
      ref,
      active: previewState.visible && previewState.id === id,
      onMouseEnter: () => {
        showPreview(ref, id, type, data);
      },
      onMouseLeave: () => {
        scheduleHide();
      },
    }),
    [previewState.visible, previewState.id, showPreview, scheduleHide]
  );

  return {
    previewState,
    showPreview,
    scheduleHide,
    cancelHide,
    hidePreview,
    previewProps,
    getTriggerProps,
  };
}

export default useReferencePreview;
